/**
 * OutputCollector — the complete, serializable VISUAL IR (docs/implementation-plan
 * Phase 6.6). Everything a renderer needs to draw a Pine script's visuals:
 * plots (with per-bar color), markers (plotshape/char/arrow), candle overlays
 * (plotcandle/plotbar), hlines, fills, and per-bar bar/background coloring — all
 * plain data so it serializes cleanly across a worker boundary.
 *
 * Values are indexed by bar index, so re-executing the realtime bar overwrites the
 * same slot (no explicit "drop uncommitted" pass needed).
 */

export interface PlotSeries {
  id: number;
  title: string;
  data: number[];
  /** Per-bar color (#RRGGBBAA) or null to use the default; sparse-filled. */
  colors: (string | null)[];
  options: Record<string, unknown>;
}

export interface MarkerPoint {
  color?: string | null;
  text?: string;
}
export interface MarkerSeries {
  id: number;
  title: string;
  kind: 'shape' | 'char' | 'arrow';
  /** abovebar | belowbar | top | bottom | absolute. */
  location: string;
  /** shape name (shape.*) or character (plotchar). */
  glyph: string;
  /** One entry per bar where the marker is shown; null when hidden. */
  data: (MarkerPoint | null)[];
}

export interface OHLC {
  open: number;
  high: number;
  low: number;
  close: number;
}
export interface CandleSeries {
  id: number;
  title: string;
  data: (OHLC | null)[];
  colors: (string | null)[];
  wickColors: (string | null)[];
  borderColors: (string | null)[];
}

export interface HLine {
  id: number;
  price: number;
  title: string;
}

export interface FillGradient {
  topValue: number[];
  bottomValue: number[];
  topColor: (string | null)[];
  bottomColor: (string | null)[];
}
export interface FillRegion {
  id: number;
  /** Ids of the two bounding plots/hlines (look up in `plots`/`hlines`). */
  plot1: number;
  plot2: number;
  title: string;
  /** Static color, or null when per-bar `colors` / `gradient` is used. */
  color: string | null;
  /** Per-bar fill color (sparse); takes precedence over `color` where present. */
  colors: (string | null)[];
  /** Per-bar vertical gradient (fill with top_value/bottom_value + colors). */
  gradient?: FillGradient;
}

export interface AlertEvent {
  bar: number;
  message: string;
}

/** A `request.security` / `request.security_lower_tf` data dependency a script asked for.
 *  A host (fractal) reads these after a discovery run to know which symbol/timeframe bars to
 *  fetch + inject (piner never fetches). `lowerTf` distinguishes the intrabar variant. */
export interface SecurityRequest {
  symbol: string;
  timeframe: string;
  lowerTf: boolean;
}

function truncateSparse<T>(values: T[], fromBar: number): void {
  let length = Math.min(values.length, fromBar);
  while (length > 0 && !(length - 1 in values)) length -= 1;
  values.length = length;
}

export class OutputCollector {
  private currentBar = 0;
  private readonly plotFirstSeen = new Map<number, number>();
  private readonly markerFirstSeen = new Map<number, number>();
  private readonly candleFirstSeen = new Map<number, number>();
  private readonly hlineFirstSeen = new Map<number, number>();
  private readonly fillFirstSeen = new Map<number, number>();
  private readonly gradientFirstSeen = new Map<number, number>();
  private readonly barColorFirstSeen = new Map<number, number>();
  private readonly bgColorFirstSeen = new Map<number, number>();
  private readonly securityFirstSeen = new Map<string, number>();

  beginBar(barIndex: number): void {
    this.currentBar = barIndex;
  }

  readonly plots = new Map<number, PlotSeries>();
  readonly markers = new Map<number, MarkerSeries>();
  readonly candles = new Map<number, CandleSeries>();
  readonly hlines = new Map<number, HLine>();
  readonly fills = new Map<number, FillRegion>();
  readonly alerts: AlertEvent[] = [];
  /** request.security[_lower_tf] data dependencies (deduped), for host-side fetch + inject. */
  readonly securityRequests: SecurityRequest[] = [];
  recordSecurityRequest(symbol: string, timeframe: string, lowerTf: boolean): void {
    const key = `${symbol}\u0000${timeframe}\u0000${lowerTf ? '1' : '0'}`;
    if (
      this.securityRequests.some(
        (r) => r.symbol === symbol && r.timeframe === timeframe && r.lowerTf === lowerTf,
      )
    ) {
      return;
    }
    this.securityFirstSeen.set(key, this.currentBar);
    this.securityRequests.push({ symbol, timeframe, lowerTf });
  }
  /** Per-bar bar coloring layers (barcolor), keyed by call-site id. */
  readonly barColors = new Map<number, (string | null)[]>();
  /** Per-bar background coloring layers (bgcolor), keyed by call-site id. */
  readonly bgColors = new Map<number, (string | null)[]>();

  // ── plot ──────────────────────────────────────────────────
  declarePlot(id: number, title: string, options: Record<string, unknown> = {}): void {
    if (!this.plots.has(id)) {
      this.plotFirstSeen.set(id, this.currentBar);
      this.plots.set(id, { id, title, data: [], colors: [], options });
    }
  }
  plot(id: number, bar: number, value: number, color?: string | null): void {
    const s = this.plots.get(id);
    if (!s) return;
    s.data[bar] = value;
    if (color !== undefined) s.colors[bar] = color;
  }

  // ── markers (plotshape / plotchar / plotarrow) ────────────
  declareMarker(
    id: number,
    title: string,
    kind: MarkerSeries['kind'],
    location: string,
    glyph: string,
  ): void {
    if (!this.markers.has(id)) {
      this.markerFirstSeen.set(id, this.currentBar);
      this.markers.set(id, { id, title, kind, location, glyph, data: [] });
    }
  }
  marker(id: number, bar: number, on: boolean, point: MarkerPoint): void {
    const s = this.markers.get(id);
    if (s) s.data[bar] = on ? point : null;
  }

  // ── candle overlays (plotcandle / plotbar) ────────────────
  declareCandle(id: number, title: string): void {
    if (!this.candles.has(id)) {
      this.candleFirstSeen.set(id, this.currentBar);
      this.candles.set(id, { id, title, data: [], colors: [], wickColors: [], borderColors: [] });
    }
  }
  candle(
    id: number,
    bar: number,
    ohlc: OHLC | null,
    color?: string | null,
    wick?: string | null,
    border?: string | null,
  ): void {
    const s = this.candles.get(id);
    if (!s) return;
    s.data[bar] = ohlc;
    if (color !== undefined) s.colors[bar] = color;
    if (wick !== undefined) s.wickColors[bar] = wick;
    if (border !== undefined) s.borderColors[bar] = border;
  }

  // ── hline / fill ──────────────────────────────────────────
  hline(id: number, price: number, title: string): void {
    if (!this.hlines.has(id)) {
      this.hlineFirstSeen.set(id, this.currentBar);
      this.hlines.set(id, { id, price, title });
    }
  }
  declareFill(id: number, plot1: number, plot2: number, title: string, color: string | null): void {
    if (!this.fills.has(id)) {
      this.fillFirstSeen.set(id, this.currentBar);
      this.fills.set(id, { id, plot1, plot2, title, color, colors: [] });
    }
  }
  fillColor(id: number, bar: number, color: string | null): void {
    const s = this.fills.get(id);
    if (s) s.colors[bar] = color; // null (na) stored too — a realtime tick may clear a fill set by an earlier tick
  }
  fillGradientPoint(
    id: number,
    bar: number,
    topValue: number,
    bottomValue: number,
    topColor: string | null,
    bottomColor: string | null,
  ): void {
    const s = this.fills.get(id);
    if (!s) return;
    if (!s.gradient) {
      this.gradientFirstSeen.set(id, this.currentBar);
      s.gradient = { topValue: [], bottomValue: [], topColor: [], bottomColor: [] };
    }
    s.gradient.topValue[bar] = topValue;
    s.gradient.bottomValue[bar] = bottomValue;
    s.gradient.topColor[bar] = topColor;
    s.gradient.bottomColor[bar] = bottomColor;
  }

  // ── bar / background coloring ─────────────────────────────
  barcolor(id: number, bar: number, color: string | null): void {
    let layer = this.barColors.get(id);
    if (!layer) {
      this.barColorFirstSeen.set(id, this.currentBar);
      this.barColors.set(id, (layer = []));
    }
    layer[bar] = color;
  }
  bgcolor(id: number, bar: number, color: string | null): void {
    let layer = this.bgColors.get(id);
    if (!layer) {
      this.bgColorFirstSeen.set(id, this.currentBar);
      this.bgColors.set(id, (layer = []));
    }
    layer[bar] = color;
  }

  /** Discard every output contribution at or after `fromBar`. */
  truncateFrom(fromBar: number): void {
    for (const [id, plot] of [...this.plots]) {
      if ((this.plotFirstSeen.get(id) ?? 0) >= fromBar) {
        this.plots.delete(id);
        this.plotFirstSeen.delete(id);
      } else {
        truncateSparse(plot.data, fromBar);
        truncateSparse(plot.colors, fromBar);
      }
    }
    for (const [id, marker] of [...this.markers]) {
      if ((this.markerFirstSeen.get(id) ?? 0) >= fromBar) {
        this.markers.delete(id);
        this.markerFirstSeen.delete(id);
      } else {
        truncateSparse(marker.data, fromBar);
      }
    }
    for (const [id, candle] of [...this.candles]) {
      if ((this.candleFirstSeen.get(id) ?? 0) >= fromBar) {
        this.candles.delete(id);
        this.candleFirstSeen.delete(id);
      } else {
        truncateSparse(candle.data, fromBar);
        truncateSparse(candle.colors, fromBar);
        truncateSparse(candle.wickColors, fromBar);
        truncateSparse(candle.borderColors, fromBar);
      }
    }
    for (const [id] of [...this.hlines]) {
      if ((this.hlineFirstSeen.get(id) ?? 0) >= fromBar) {
        this.hlines.delete(id);
        this.hlineFirstSeen.delete(id);
      }
    }
    for (const [id, fill] of [...this.fills]) {
      if ((this.fillFirstSeen.get(id) ?? 0) >= fromBar) {
        this.fills.delete(id);
        this.fillFirstSeen.delete(id);
        this.gradientFirstSeen.delete(id);
        continue;
      }
      truncateSparse(fill.colors, fromBar);
      if (fill.gradient) {
        if ((this.gradientFirstSeen.get(id) ?? 0) >= fromBar) {
          delete fill.gradient;
          this.gradientFirstSeen.delete(id);
        } else {
          truncateSparse(fill.gradient.topValue, fromBar);
          truncateSparse(fill.gradient.bottomValue, fromBar);
          truncateSparse(fill.gradient.topColor, fromBar);
          truncateSparse(fill.gradient.bottomColor, fromBar);
        }
      }
    }
    for (const [id, colors] of [...this.barColors]) {
      if ((this.barColorFirstSeen.get(id) ?? 0) >= fromBar) {
        this.barColors.delete(id);
        this.barColorFirstSeen.delete(id);
      } else {
        truncateSparse(colors, fromBar);
      }
    }
    for (const [id, colors] of [...this.bgColors]) {
      if ((this.bgColorFirstSeen.get(id) ?? 0) >= fromBar) {
        this.bgColors.delete(id);
        this.bgColorFirstSeen.delete(id);
      } else {
        truncateSparse(colors, fromBar);
      }
    }

    let alertWrite = 0;
    for (const event of this.alerts) {
      if (event.bar < fromBar) this.alerts[alertWrite++] = event;
    }
    this.alerts.length = alertWrite;

    let securityWrite = 0;
    for (const request of this.securityRequests) {
      const key = `${request.symbol}\u0000${request.timeframe}\u0000${request.lowerTf ? '1' : '0'}`;
      if ((this.securityFirstSeen.get(key) ?? 0) < fromBar) {
        this.securityRequests[securityWrite++] = request;
      } else {
        this.securityFirstSeen.delete(key);
      }
    }
    this.securityRequests.length = securityWrite;
    this.currentBar = fromBar;
  }

  alert(bar: number, message: string): void {
    this.alerts.push({ bar, message });
  }
}
